import confetti from 'canvas-confetti';
import { i18n } from '~/modules/i18n';
import { swalNotification } from './swal';

export function randomInRange(min: number, max: number) {
    return Math.random() * (max - min) + min;
}

export function confettiCannon() {
    const colors = ['#9B7F4A', '#B69B67', '#C3AD83'];
    const end = Date.now() + 1500;
    const defaults = { startVelocity: 20, spread: 360, ticks: 60, zIndex: 99999, particleCount: 60 * 2 };

    const interval: any = setInterval(() => {
        if (Date.now() > end) {
            return clearInterval(interval);
        }
        void confetti({ colors, ...defaults, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
        void confetti({ colors, ...defaults, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
    }, 100);
}

export function mulberry32(seed: number) {
    return function() {
        seed = seed + 0x9e3779b9 | 0;
        let z = seed;
        z ^= z >>> 16;
        z = Math.imul(z, 0x21f0aaad);
        z ^= z >>> 15;
        z = Math.imul(z, 0x735a2d97);
        z ^= z >>> 15;
        return (z >>> 0) / 0x100000000;
    };
}

/**
 * Generate a random color based on a set seed
 *
 * @param seed mulberry seed
 * @returns Random color hex
 */
export function randomSeededRGBColor(seed: number) {
    const random = mulberry32(seed);
    return randomRGBColor(random());
}

/**
 * Generate a random rgb color
 *
 * @param random number between 0 and 1
 * @returns random hex
 */
export function randomRGBColor(random = Math.random()) {
    return '#' + Math.floor(random * 0xFFFFFF).toString(16).padEnd(6, '0');
}

/**
 * Generate a random hsl color
 *
 * @param random number between 0 and 1
 * @returns {string} random hex
 */
export function randomHSLColor(random = Math.random): string {
    return `hsl(${Math.floor(360 * random())},${70 + random() * 20}%,${70 + random() * 10}%)`;
}

/**
 * Generate a random color based on a set seed
 *
 * @param seed mulberry seed
 * @returns {string} Random hsl color
 */
export function randomSeededPastelColor(seed: number): string {
    return randomHSLColor(mulberry32(seed));
}

export async function copy(value: string | undefined | null, succesMessage?: string | null) {
    if (!value) return;

    const t = i18n.global.t;
    // Only works on secure origins (https)
    // Or edit chrome flags {@link chrome://flags/#unsafely-treat-insecure-origin-as-secure}
    if (navigator.clipboard) {
        try {
            await navigator.clipboard.writeText(value);

            void swalNotification(succesMessage || t('basic.copied'));
        } catch {
            void swalNotification(t('swal.copy_error'), true);
        }
    } else {
        // This is unreliable
        const element = document.createElement('textarea');
        element.value = value;
        element.setAttribute('readonly', '');
        element.style.position = 'absolute';
        element.style.left = '-9999px';
        document.body.appendChild(element);
        element.select();
        element.setSelectionRange(0, 99999);
        document.execCommand('copy');
        document.body.removeChild(element);

        void swalNotification(succesMessage || t('basic.copied'));
    }
}

export async function copyHtml(html: string | undefined | null, succesMessage?: string | null) {
    if (!html) return;

    const t = i18n.global.t;
    try {
        const plainText = stripHTML(html);
        await navigator.clipboard.write([
            new ClipboardItem({
                'text/html': new Blob([html], { type: 'text/html' }),
                'text/plain': new Blob([plainText], { type: 'text/plain' }),
            }),
        ]);
        void swalNotification(succesMessage || t('basic.copied'));
    } catch {
        void swalNotification(t('swal.copy_error'), true);
    }
}

export function stripHTML(html?: string | null) {
    if (!html) return '';
    const el = document.createElement('div');
    el.innerHTML = html;
    for (const br of el.querySelectorAll('br')) {
        br.replaceWith('\n');
    }
    for (const block of el.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6, li, blockquote')) {
        block.after('\n');
    }
    return (el.textContent ?? '').trim();
}

/**
 * Generate a random string of a given length with options for numbers, special characters, and capitalization.
 *
 * @export
 * @param {number} [length=8]
 * @param {boolean} [numbers=true]
 * @param {boolean} [specialCharacters=true]
 * @param {boolean} [capitalized=true]
 * @return {string}
 */
export function generateString(
    length = 8,
    numbers = true,
    specialCharacters = true,
    capitalized = true,
): string {
    let chars = 'abcdefghijklmnopqrstuvwxyz';
    if (capitalized) chars += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (numbers) chars += '0123456789';
    if (specialCharacters) chars += '!@#$%^&*()_+[]{}|;:,.<>?';

    return Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}
